import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams, useNavigate } from "react-router-dom";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { ActionButtonConfig } from "../../../../../../shared/components/modals/action-popup";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../../../shared/app-configs/sc-app-config";
import { 
  IProductCategoryInformation, 
  IProductCategoryApiResponse, 
  mapProductCategoryPayload,
  initialProductCategoryInformation,
  mapProductCategoryApiResponseToInitialValues
} from "../../../sc-models/product-category-information-dto";

const validationSchema = Yup.object().shape({
  scProductCategoryCode: Yup.string().required("SC Product Category Code is required"),
  scProductCategoryUserSpecificTerm: Yup.string().nullable(),
});

interface ProductCategoryInformationTabProps {
  onNextTab?: () => Promise<void>;
  scStatus?: boolean;
}

const ProductCategoryInformationTab: React.FC<ProductCategoryInformationTabProps> = ({ onNextTab, scStatus }) => {
  const { formtype, SC_ID } = useParams();
  const navigate = useNavigate();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const isReadOnlyBasedOnStatus = hasSubmitSC || scStatus;
  const [data, setData] = useState<IProductCategoryInformation[]>([]);
  const [tableData, setTableData] = useState<IProductCategoryInformation[]>([]);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [deletingRowIndex, setDeletingRowIndex] = useState<number>(-1);
  const [productCategoryCodeOptions, setProductCategoryCodeOptions] = useState<OptionType[]>([]);
  const { notify } = useNotification();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();
  const { executePost, loading: isSaving } = useApi();
  const { executeGet, executeDelete, loading: isLoading } = useApi();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popUpTitle, setPopUpTitle] = useState("");
  const [popUpMessage, setPopUpMessage] = useState("");
  const [popUpBtnName, setPopUpBtnName] = useState("");

  // Fetch product category codes from API
  const getProductCategoryCodes = async () => {
    try {
      const result: any = await dtrackItServiceV4.getProductCategory();
      const options = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
        code: t.code_1,
      }));
      setProductCategoryCodeOptions(options);
    } catch (err) {
      Utills.cl(`Error in Fetching Product Category Codes ${err}`);
      notify('Error fetching product category codes', 'error');
    }
  };

  // Fetch existing product category information from API
  const getProductCategoryInformation = async () => {
    if (!SC_ID) return;
    
    try {
      const response = await executeGet(API_ROUTES.GET_PRODUCT_CATEGORY_INFORMATION_V4, { scId: SC_ID });
      
      if (response?.status === 200 && response.data) {
        const apiData: IProductCategoryApiResponse = response.data;
        
        if (apiData.response && apiData.response.length > 0) {
                     // Transform API response to component data format
           const transformedData: IProductCategoryInformation[] = apiData.response.map((item: any) => ({
             productCategoryId : item.productCategoryId,
             scProductCategoryCode: item.scProductCategoryCode || "",
             scProductCategoryUserSpecificTerm: item.scProductCategoryUserSpecificTerm || "",
             scProductCategoryCodeDescription: item.scProductCategoryCodeDescription || "",
           }));
          
          setData(transformedData);
          setTableData(transformedData);
        }
      }
    } catch (error) {
      console.error('Error fetching product category information:', error);
      notify('Error fetching product category information', 'error');
    }
  };

  useEffect(() => {
    getProductCategoryCodes();
    getProductCategoryInformation();
  }, [SC_ID]);

  const formik = useFormik<IProductCategoryInformation>({
    initialValues: initialProductCategoryInformation,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {

    if(columnId === 'scProductCategoryCode') {
      let optionCode = productCategoryCodeOptions
        .find(val => val.value === value)?.code || "";
      formik.setFieldValue(columnId, value);
      // Set the description field when dropdown value changes
      const selectedOption = productCategoryCodeOptions.find(option => option.value === value);
      if (selectedOption) {
        formik.setFieldValue('scProductCategoryCodeDescription', selectedOption.label);
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, [columnId]: value, scProductCategoryCodeDescription: selectedOption.label } : row
          )
        );
      } else {
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, [columnId]: value } : row
          )
        );
      }
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
  };

  const handleAddNewRow = () => {
    const newRow: IProductCategoryInformation = initialProductCategoryInformation;
    setRestrictPopUpSave(true);
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setRestrictPopUpSave(false);
    setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null || !formik.isValid) {
      notify('Enter the mandatory fields', 'error');
      return;
    }

    try {
      const updatedRow = formik.values;
      
      // Get the existing row data to check if it has productCategoryId 
      const existingRow = data[editingRowIndex];
      
             // Call the API to save the product category information
       const payload = mapProductCategoryPayload(updatedRow, SC_ID!); // Assert SC_ID is non-null
       if (existingRow.productCategoryId ) {
         payload.productCategoryId = existingRow.productCategoryId ; // Include productCategoryId if editing existing row
       }

      const response = await executePost(API_ROUTES.POST_PRODUCT_CATEGORY_INFORMATION_V4, payload);
      
      if (response?.status === 200 || response?.status === 201) {
        // Update the row with the new data while preserving the productCategoryId 
        const newData = data.map((row, index) =>
            index === editingRowIndex ? { ...updatedRow, productCategoryId : existingRow.productCategoryId  } : row
          );
        setData(newData);
        setTableData(newData);

        setRestrictPopUpSave(false);
        setEditingRowIndex(-1);
        formik.resetForm();
        
        // Refresh data from API to ensure it stays updated
        await getProductCategoryInformation();
        
        notify('Product category information saved successfully', 'success');
      } else {
        notify('Error saving product category information', 'error');
      }
    } catch (error) {
      console.error('Error saving product category information:', error);
      notify('Error saving product category information', 'error');
    }
  };

  const handleNavigateToNextTab = () => {
    navigate(`/v4/sc/${formtype}/product-detail-information/${SC_ID}`);
  };

  const handleNext = async () => {
    if (data.length === 0) {
      notify('Please add at least one Product Category', 'error');
      return;
    }
    
    if (onNextTab) {
      await onNextTab();
    }
    handleNavigateToNextTab();
  };

  const handleCancelSave = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, []);

  const columns: EditableColumnDef<IProductCategoryInformation>[] = [
    {
      accessorKey: "scProductCategoryCode",
      header: "SC Product Category Code",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Product Category Code</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="scProductCategoryCode"
          value={value}
          placeholder="Select Product Category Code"
          options={productCategoryCodeOptions}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "scProductCategoryUserSpecificTerm",
      header: "SC Product Category User Specific Term",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="scProductCategoryUserSpecificTerm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Product Category User Specific Term"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    
    // Ensure all required fields are set in formik including description
    const rowData = data[rowIndex];
    const formikValues = {
      scProductCategoryCode: rowData.scProductCategoryCode || "",
      scProductCategoryCodeDescription: rowData.scProductCategoryCodeDescription || "",
      scProductCategoryUserSpecificTerm: rowData.scProductCategoryUserSpecificTerm || "",
    };
    
    formik.setValues(formikValues);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const rowToDelete = data[rowIndex];
    
    // Check if the row has a productCategoryId  (exists in database)
    if (rowToDelete.productCategoryId ) {
      // Show confirmation popup for API deletion
      setPopUpTitle("Confirm Delete");
      setPopUpMessage("Are you sure you want to delete this product category? This action cannot be undone.");
      setPopUpBtnName("Delete");
      setDialogOpen(true);
      // Store the row index for deletion after confirmation
      setDeletingRowIndex(rowIndex);
    } else {
      // Delete locally if no productCategoryId  (new row not yet saved)
      const updatedData = data.filter((_, index) => index !== rowIndex);
      setData(updatedData);
      setTableData(updatedData);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const rowIndex = deletingRowIndex;
      const rowToDelete = data[rowIndex];
      
      if (rowToDelete.productCategoryId ) {
        // Call API to delete from database
        const response = await executeDelete(
          API_ROUTES.DELETE_API_V4,
          {
            scId: SC_ID,
            productCategoryId: rowToDelete.productCategoryId 
          }
        );
        
        if (response?.status === 200) {
          // Remove from local state after successful API deletion
          const updatedData = data.filter((_, index) => index !== rowIndex);
          setData(updatedData);
          setTableData(updatedData);
          
          // Refresh data from API to ensure it stays updated
          await getProductCategoryInformation();
          
          notify('Product category deleted successfully', 'success');
        } else {
          notify('Failed to delete product category', 'error');
        }
      }
    } catch (error) {
      console.error('Error deleting product category:', error);
      notify('Error deleting product category', 'error');
    } finally {
      // Reset states
      setDialogOpen(false);
      setDeletingRowIndex(-1);
    }
  };

  const actions: ActionButtonConfig[] = [
    {
      label: "Home",
      onClick: () => {
        navigate('/sc-dashboard');
      },
      variant: "contained",
      color: "default",
    },
    {
      label: "Back",
      onClick: () => {
        navigate(`/v4/sc/${formtype}/facility-details/${SC_ID}`);
      },
      variant: "contained" as const,
      color: "default" as const,
    },
    {
      label: "Next",
      onClick: handleNext,
      disabled: isRestrictPopUpSave || isSaving,
      variant: "contained",
      color: "primary",
    },
  ];

  return (
    <>
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <div>
          <Box
            component="main"
            sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
          >
            <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
              <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={!formik.isValid || isReadOnlyBasedOnStatus || isRestrictPopUpSave}>
                Add Product Category
              </StyledButton>
            </Box>
            
            <EditableTable
              data={data}
              columns={columns}
              onSaveRow={handleSaveRow}
              onCellChange={handleCellChange}
              editingRowIndex={editingRowIndex}
              onEditRow={handleEditRow}
              onCancelEdit={handleCancelEdit}
              onDeleteRow={handleDeleteRow}
              enableEditing={!isReadOnlyBasedOnStatus}
              isSaving={isSaving}
            />
            
          </Box>

          <div style={{ marginTop: "3rem" }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              {actions.map((action, index) => (
                <StyledButton
                  key={index}
                  onClick={action.onClick}
                  variant={action.variant || "text"}
                  disabled={action.disabled}
                >
                  {action.label}
                </StyledButton>
              ))}
            </Box>
          </div>

          <AlertPopup
            open={dialogOpen}
            title={popUpTitle}
            message={popUpMessage}
            onClose={handleCancelSave}
            onConfirm={handleConfirmDelete}
            confirmText={popUpBtnName}
            cancelText="Cancel"
          />
        </div>
      )}
    </>
  );
};

export default ProductCategoryInformationTab;
